/* eslint-disable @typescript-eslint/naming-convention */

import {FastFilter, FilterChoose, FilterDateRange, FilterFlag, FilterRange} from 'models';
import {TWith} from 'core/types';
import {createFilterActions} from 'plugins/modules/filter/actions';
import {FilterAdapter} from 'plugins/modules/filter/services/FilterAdapter';

export interface ITagData {
    tagCode: string;
    tagId: string;
}

export interface IFilterState {
    filterList: TFilter[];
    comboFilterList: FastFilter[];
    isFilterPanelVisible: boolean;
    tabs?: {code: string; title: string; text?: string}[];
}

export type TSearchFilter = typeof FilterAdapter.SEARCH_CATEGORY_TYPE | typeof FilterAdapter.SEARCH_STORE_TYPE;

export interface IFilterSearchCategory extends Omit<FilterChoose, 'type'> {
    type: TSearchFilter;
}

export interface IFilterSearchStore extends Omit<FilterChoose, 'type'> {
    type: TSearchFilter;
}

export type TFilter =
    | FilterRange
    | FilterChoose
    | FilterFlag
    | IFilterSearchStore
    | IFilterSearchCategory
    | FilterDateRange
    | TFilterCheckbox;

export type TFilterCheckbox = {
    id: number;
    title: string;
    type: 'checkbox';
    hint?: string;
};
export type TWithFilter<T> = TWith<T, IFilterState>;

export type TBuildRangeQueryData = Record<string, string>;

export type TFilterActions = ReturnType<typeof createFilterActions>;

export type TFilterValue = string | undefined;

export type TFilterRangeValue = [TFilterValue, TFilterValue];

export interface IFilter {
    code: string;
    values: TFilterValue[];
    valuesJoinType: FilterValuesJoinType;
}

// eslint-disable-next-line no-shadow
export enum FilterType {
    ALONE_CHECKBOX = 'ALONE_CHECKBOX',
    CHECKBOX_AND = 'CHECKBOX_AND',
    CHECKBOX_OR = 'CHECKBOX_OR',
    CHECKBOX_OR_WINERY = 'CHECKBOX_OR_WINERY',
    CHECKBOX_WITH_DESC = 'CHECKBOX_WITH_DESC',
    RANGE = 'RANGE',
    RANGE_WITH_GROUP = 'RANGE_WITH_GROUP',
    EXPRESS_ORDER = 'EXPRESS_ORDER',
}

// eslint-disable-next-line no-shadow
export enum FilterCustomViewType {
    ART = 'art',
    NONE = '0',
    CHIPS = 'chips',
    GIFT = 'gift',
    NO_COUNT = 'no_count',
    STARS = 'stars',
}

type TDay = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

interface IDaySchedule {
    day: TDay;
    from: string;
    to: string;
}

interface IMetroStation {
    hexColor: string;
    name: string;
    latitude: string;
    longitude: string;
}

export interface IExpressOrder {
    id?: string;
    cityId: number;
    idVinoteki: number;
    isWineryExpressAvailable: boolean;
    titleVinoteki: string;
    typedSchedule: IDaySchedule[];
    warehouseCode: string;
    metroStations: IMetroStation[];
    geoposition: {latitude: string; longitude: string};
    address: string;
}

export interface IExpressOrderResponse {
    data: IExpressOrder;
}

export interface IGetExpressOrderResponse {
    data: IExpressOrder;
    isHidden: boolean;
}

export interface IFilterFacetResponse {
    items: IFilterFacetGroup[];
    top_filter?: IFilterFacetTopFilter[];
    sections?: Record<string, IFilterFacetSection>;
    tabs?: {code: string; title: string; text?: string}[];
}

export interface IFilterFacetEnum {
    code: string;
    count: number;
    isApplied: boolean;
    max: number;
    min: number;
    title: string;
}

export interface IFilterFacetVariant {
    code: string | number;
    count: number;
    isApplied: boolean;
    title: string | number;
}

export interface IFilterFacet {
    isApplied: boolean;
    search: boolean;
    code: string;
    customView: FilterCustomViewType;
    enum?: IFilterFacetEnum[];
    hint: string;
    isCollapsed: boolean;
    isMultiple: boolean;
    maxRange?: number;
    minRange?: number;
    maxValue?: number | null;
    minValue?: number | null;
    sort: number;
    title: string;
    type: FilterType;
    unit: string;
    variants?: IFilterFacetVariant[];
}

export interface IFilterFacetGroup {
    group: string;
    groupExpand: boolean;
    groupHint: string | null;
    groupName: string;
    groupSearch: boolean;
    items: IFilterFacet[];
    searchPlaceholder: string | null;
}

export interface IFilterFacetTopFilter {
    filterAlias: string;
    filterName: string;
    priority: string;
}

export interface IFilterFacetSection {
    count: number;
    code: string;
    title: string;
    active: boolean;
}

// eslint-disable-next-line no-shadow
export enum FilterValuesJoinType {
    AND = 'and',
    OR = 'or',
    RANGE = 'range',
    SINGLE_VALUE = 'SINGLE_VALUE',
}

export type TFilterAloneCheckboxCustomView = FilterCustomViewType.GIFT | FilterCustomViewType.ART;

export interface IFilterGroup {
    [key: string]: IFilterGroupItem;
}

export interface IFilterGroupItem {
    groupSearchPlaceholder?: string;
    groupWithCollapse: boolean;
    title: string;
    withSearch?: boolean;
}

export interface IFilterConfig {
    code: string;
    group: IFilterGroupItem | null;
    itemWithCollapse: boolean;
    itemWithSearch?: boolean;
}
